import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, PhoneCall, User } from 'lucide-react';
import LiveTranslatorAnimation from './LiveTranslatorAnimation';

const PhoneAnimation = () => {
  // Stage machine:
  //   'ringing'     — incoming call (0–2s)
  //   'connected'   — call answered, small waveform bars (2–5s)
  //   'translating' — final stage: Live Translator waveform on screen (5–10s)
  const [stage, setStage] = useState('ringing');
  const isRinging = stage === 'ringing';
  const callConnected = stage === 'connected';
  const isTranslating = stage === 'translating';

  useEffect(() => {
    // Run one full cycle then loop.
    const cycle = () => {
      setStage('ringing');
      const t1 = setTimeout(() => setStage('connected'), 2000);
      const t2 = setTimeout(() => setStage('translating'), 5000);
      return [t1, t2];
    };

    let timers = cycle();
    const loop = setInterval(() => {
      timers.forEach(clearTimeout);
      timers = cycle();
    }, 10000);

    return () => {
      timers.forEach(clearTimeout);
      clearInterval(loop);
    };
  }, []);

  return (
    <div className="relative w-full mx-auto" style={{ maxWidth: '19rem' }}>
      {/* Vibration wrapper — scoped CSS animation runs the 2s-on / 10s-off
          micro-shake loop without conflicting with Framer Motion's inline
          transforms on the child. */}
      <div className="phone-vibrate">
        {/* Phone Frame */}
        <motion.div
          className="relative bg-black rounded-[3rem] p-3 shadow-2xl"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          style={{
            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)',
          }}
        >
        {/* Phone Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-black rounded-b-2xl z-10" />
        
        {/* Phone Screen */}
        <div className="relative bg-white rounded-[2.5rem] overflow-hidden" style={{ aspectRatio: '9/19' }}>
          {/* Status Bar */}
          <div className="absolute top-0 left-0 right-0 h-12 flex items-center justify-between px-8 text-black/70 text-xs z-20">
            <span>9:41</span>
            <div className="flex gap-1">
              <div className="w-4 h-3 border border-black/30 rounded-sm" />
              <div className="w-2 h-3 bg-black/30 rounded-sm" />
            </div>
          </div>

          {/* Call Screen Content */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7] flex flex-col items-center justify-center px-6">
            
            {/* Caller Info */}
            <AnimatePresence mode="wait">
              {(isRinging || callConnected) && (
                <motion.div
                  key="caller"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="text-center mb-8"
                >
                  {/* Avatar */}
                  <motion.div
                    className="w-24 h-24 rounded-full bg-[#E8A0B8]/20 flex items-center justify-center mx-auto mb-4"
                    animate={isRinging ? { scale: [1, 1.05, 1] } : {}}
                    transition={{ repeat: Infinity, duration: 1 }}
                  >
                    <User className="w-12 h-12 text-[#E8A0B8]" />
                  </motion.div>

                  {/* Name */}
                  <h3 className="text-2xl font-medium text-black/90 mb-2">
                    GoogLyn
                  </h3>
                  
                  {/* Status */}
                  <p className="text-black/60">
                    {isRinging ? 'Incoming call...' : 'Connected'}
                  </p>

                  {/* Restaurant Assistant Badge */}
                  <div className="mt-3 inline-block px-4 py-1 bg-white/80 rounded-full">
                    <p className="text-xs text-black/60">Restaurant Assistant</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Ringing Animation Circles */}
            {isRinging && (
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -mt-8">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    className="absolute w-32 h-32 rounded-full border-2 border-[#E8A0B8]/30"
                    initial={{ scale: 1, opacity: 0.5 }}
                    animate={{ scale: 2.5, opacity: 0 }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.4,
                    }}
                    style={{
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                ))}
              </div>
            )}

            {/* Call Actions */}
            <div className="absolute bottom-20 left-0 right-0 flex justify-center gap-6">
              {isRinging && (
                <>
                  {/* Decline Button */}
                  <motion.button
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    whileHover={{ scale: 1.1 }}
                    className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center shadow-lg"
                  >
                    <Phone className="w-6 h-6 text-white rotate-[135deg]" />
                  </motion.button>

                  {/* Accept Button */}
                  <motion.button
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    whileHover={{ scale: 1.1 }}
                    className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center shadow-lg"
                  >
                    <PhoneCall className="w-6 h-6 text-white" />
                  </motion.button>
                </>
              )}

              {callConnected && (
                <motion.button
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  whileHover={{ scale: 1.1 }}
                  className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center shadow-lg"
                >
                  <Phone className="w-6 h-6 text-white rotate-[135deg]" />
                </motion.button>
              )}
            </div>

            {/* Audio Waveform - when connected */}
            {callConnected && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute bottom-40 flex gap-1 items-end h-12"
              >
                {[...Array(15)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="w-1 bg-[#E8A0B8] rounded-full"
                    animate={{
                      height: ['20%', '100%', '40%', '80%', '30%'],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      delay: i * 0.1,
                    }}
                  />
                ))}
              </motion.div>
            )}

            {/* Final stage — Live Translator waveform overlaying the phone screen */}
            <AnimatePresence>
              {isTranslating && (
                <motion.div
                  key="translator-stage"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0 flex items-center justify-center px-3 z-30"
                  data-testid="phone-translator-stage"
                >
                  <div className="w-full">
                    <LiveTranslatorAnimation />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Phone Buttons */}
        <div className="absolute right-0 top-28 w-1 h-12 bg-black/50 rounded-l-sm" />
        <div className="absolute right-0 top-44 w-1 h-12 bg-black/50 rounded-l-sm" />
        <div className="absolute left-0 top-36 w-1 h-16 bg-black/50 rounded-r-sm" />
      </motion.div>
      </div>
    </div>
  );
};

export default PhoneAnimation;
